import { useState } from "react";
import { useAuth } from "../contexts/useAuth.jsx";
import { toast } from "sonner";
import { useTranslation } from "../contexts/TranslationContext";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { passwordValidation } from "../utils/validation.js";

export default function ChangePassword() {
  const { t } = useTranslation();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useAuth();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }),
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success(responseData.message || t('changePassword.success'));
        reset();
      } else {
        toast.error(responseData.error || t('changePassword.error'));
      }
    } catch (err) {
      console.error("Erro:", err);
      toast.error(t('changePassword.connectionError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-center text-white mb-6">
          {t('changePassword.title')}
        </h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                placeholder={t('changePassword.currentPassword')}
                {...register("currentPassword", passwordValidation(t))}
                className={`w-full p-3 pr-12 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                  errors.currentPassword ? 'border-red-500' : 'border-white/10'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-500 transition"
              >
                {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder={t('changePassword.newPassword')}
                {...register("newPassword", passwordValidation(t))}
                className={`w-full p-3 pr-12 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                  errors.newPassword ? 'border-red-500' : 'border-white/10'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-500 transition"
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.newPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-500 hover:bg-brand-400 text-black p-3 rounded-md transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="animate-spin" size={20} />}
            {t('changePassword.button')}
          </button>
        </form>
      </div>
    </div>
  );
}
